import { FormSelectSelectionLabel } from '../../../../common/form/input/FormSelectSelectionLabel';
import { formatCodeName } from '../../../../common/utils';
import type { AlgorithmMappingQueryDtoType } from '../../mappings/schema/types';
import { AlgorithmType } from '../../schema/zAlgorithmType';
import { AlgorithmSourceFormDataType } from '../../sources/schema/types';
import { getAlgorithmSourceLabel } from '../../utils';
import {
  decodeRuleSourceItemValue,
  encodeRuleAlgorithmMappingValue,
  encodeRuleAlgorithmSourceValue,
} from '../utils';
import type {
  AlgorithmTargetMappingSourceOption,
  ResolvedSourceValue,
  ResolvedTargetValue,
  SourceDimensionsAndIndicatorsByCode,
} from './types';
import {
  DataObjectFormDataType,
  DataObjectType,
} from '../../../../dataobject/types';

export const resolveSourceValue = (
  currentSourceValue: string | null | undefined,
  subjectAttributeCodes: string[],
  mappings: AlgorithmMappingQueryDtoType[],
): ResolvedSourceValue => {
  const sourceValue = decodeRuleSourceItemValue(currentSourceValue) as any;
  const mappingId = sourceValue.algorithmMappingId;
  const mapping = mappingId
    ? mappings.find((item) => item.id === mappingId)
    : undefined;

  return {
    algorithmSourceId: sourceValue.algorithmSourceId,
    dimensionOrIndicatorCode: sourceValue.sourceItemCode,
    isSubjectAttribute: subjectAttributeCodes.includes(
      sourceValue.sourceItemCode,
    ),
    mappingId,
    classifierIdTo: mapping?.classifierIdTo,
  };
};

export const getSourceOptions = ({
  algorithmTypeCode,
  dataObjectVersion,
  targetValue,
  sources,
  mappings,
}: {
  algorithmTypeCode: AlgorithmType;
  dataObjectVersion: DataObjectFormDataType;
  targetValue: ResolvedTargetValue;
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
}) => {
  const sourceOptions: AlgorithmTargetMappingSourceOption[] = [];

  const addSourceOption = (
    algorithmSourceLabel: string,
    algorithmSourceId: number,
    entity: { code: string; name: string },
  ) =>
    sourceOptions.push({
      value: encodeRuleAlgorithmSourceValue(algorithmSourceId, entity.code),
      codeName: formatCodeName(entity),
      label: (
        <FormSelectSelectionLabel ellipsis supertitle={algorithmSourceLabel}>
          {formatCodeName(entity)}
        </FormSelectSelectionLabel>
      ),
    });

  const excludeAllDimensions = !shouldIncludeDimensions(
    algorithmTypeCode,
    targetValue,
  );

  const excludeIndicators = !shouldIncludeIndicators(
    algorithmTypeCode,
    targetValue,
  );

  const sourceDimensionsAndIndicatorsByCode: SourceDimensionsAndIndicatorsByCode =
    new Map();

  for (const algorithmSource of sources) {
    const { id, dimensions, indicators } = algorithmSource;
    const algorithmSourceLabel = getAlgorithmSourceLabel(algorithmSource);
    const selfSource =
      'dataObjectDescription' in algorithmSource &&
      algorithmSource.dataObjectDescription &&
      algorithmSource?.dataObjectDescription.id ===
        dataObjectVersion.dataObjectId;

    if (!sourceDimensionsAndIndicatorsByCode.has(id)) {
      sourceDimensionsAndIndicatorsByCode.set(id, new Map());
    }

    for (const dimension of dimensions) {
      sourceDimensionsAndIndicatorsByCode
        .get(id)!
        .set(dimension.code, dimension);

      if (excludeAllDimensions) {
        continue;
      }

      if (
        dataObjectVersion.dataObjectType === DataObjectType.IN &&
        algorithmTypeCode === AlgorithmType.INSERT &&
        dimension.code === 'SUBJEKT'
      ) {
        continue;
      }

      if (targetValue.isSubject && dimension.code !== 'SUBJEKT') {
        // SUBJEKT dimensions can only be paired with other SUBJEKT dimensions
        continue;
      }

      // If a CLASSIFIER dimension is already selected, then
      // exclude dimensions that do not have the same classifier
      if (
        targetValue.classifierId &&
        targetValue.classifierId !== dimension.classifier?.id
      ) {
        continue;
      }

      // If a SUBJECT_ATTRIBUTE dimension is already selected, then
      // exclude dimensions that do not have the same classifier
      if (
        targetValue.subjectAttributeCode &&
        targetValue.subjectAttributeCode !== dimension.code
      ) {
        continue;
      }

      addSourceOption(algorithmSourceLabel, id, dimension);
    }

    for (const indicator of indicators) {
      sourceDimensionsAndIndicatorsByCode
        .get(id)!
        .set(indicator.code, indicator);

      if (
        excludeIndicators ||
        targetValue.hasDimensions ||
        targetValue.hasConfidentialityStatuses ||
        (indicator.intermediate && !selfSource)
      ) {
        continue;
      }

      addSourceOption(algorithmSourceLabel, id, indicator);
    }
  }

  const excludeAllMappings = excludeAllDimensions || targetValue.isSubject;

  if (!excludeAllMappings) {
    for (const { id, classifierIdTo, classifierToCode, alias } of mappings) {
      // If a classifier dimension is selected as target, then
      // exclude mappings that do not have the same classifier as target classifier ("classifier 2")
      if (
        targetValue.classifierId &&
        targetValue.classifierId !== classifierIdTo
      ) {
        continue;
      }

      // If a SUBJECT_ATTRIBUTE dimension is selected as target, then
      // exclude mappings that do not have the same SUBJECT_ATTRIBUTE classifier as target classifier ("classifier 2")
      if (
        targetValue.subjectAttributeCode &&
        targetValue.subjectAttributeCode !== classifierToCode
      ) {
        continue;
      }

      sourceOptions.push({
        value: encodeRuleAlgorithmMappingValue(id),
        codeName: alias,
        label: alias,
      });
    }
  }

  return { sourceOptions, sourceDimensionsAndIndicatorsByCode };
};

const shouldIncludeDimensions = (
  algorithmTypeCode: AlgorithmType,
  targetValue: ResolvedTargetValue,
) => {
  if (algorithmTypeCode === AlgorithmType.UPDATE_INDICATOR) {
    return false;
  }

  return !targetValue.hasIndicators;
};

const shouldIncludeIndicators = (
  algorithmTypeCode: AlgorithmType,
  targetValue: ResolvedTargetValue,
) => {
  if (algorithmTypeCode === AlgorithmType.UPDATE_DIMENSION) {
    return false;
  }

  return !targetValue.hasDimensions;
};
